"use client";

import { usePathname } from "next/navigation";

import { Icon, Link, Text, View } from "@/components";
import { useLayout } from "@/providers";

import type { MenuItem } from "@/components/types";
import type { JSX } from "react";
import type { CollapsibleProps } from "./types";

/**
 * @component
 * @name Collapsible
 * @description Renders an accessible Collapsible menu using a native HTML select element with custom styling.
 * This approach ensures better accessibility across all devices, including mobile and screen readers.
 *
 * @example
 * ```tsx
 * const [filter, setFilter] = useState("Option 1");
 *
 * <Collapsible
 *   items={["Option 1", "Option 2", "Option 3"]}
 *   selected={filter}
 *   setSelected={setFilter}
 *   className="w-48 bg-white dark:bg-slate-800"
 * />
 * ```
 *
 * @param {CollapsibleProps} props - The properties for the Collapsible component.
 * @param {string[]} props.items - The list of string items to display in the Collapsible menu.
 * @param {string} [props.selected] - The currently selected value (for controlled mode).
 * @param {function} [props.setSelected] - Callback function called when an item is selected (for controlled mode). Receives the selected string value.
 * @param {string} [props.className] - Additional CSS classes to apply to the Collapsible select. Defaults to full width and themed background.
 * @param {string} [props.id] - The id attribute for the select element.
 * @param {string} [props.defaultValue] - The default value for uncontrolled mode.
 * @returns {JSX.Element} The rendered Collapsible component.
 */
const Collapsible = ({
  className,
  id,
  items,
  children,
  icon,
  label
}: CollapsibleProps): JSX.Element => {
  const { t } = useLayout();
  const path = usePathname();

  return (
    <View
      className={`flex flex-col gap-2 ${className || "w-full bg-white dark:bg-slate-800"}`}
    >
      {children}
      <View key={id} className="group/item flex flex-col">
        <Text className="px-4 py-3 rounded-2xl text-gray-500 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block">
          <Icon name={String(icon)} /> {t(label || "")}
        </Text>
        <View className="flex flex-col gap-2 ml-4">
          {items.map((subItem: MenuItem) => {
            const isSubActive = path === String(subItem.value);
            return (
              <Link
                key={subItem.id}
                href={String(subItem.value)}
                target={subItem.target ?? "_self"}
                className={`
                            group/item flex items-center px-4 py-3 rounded-2xl transition-[background-color,color,transform] duration-200 relative overflow-hidden
                            xxl:justify-center group-hover:justify-start
                            ${isSubActive ? "active" : ""}
                          `}
              >
                <View
                  className={`w-6 h-6 flex justify-center items-center text-lg z-10 shrink-0 ${isSubActive ? "active" : ""}`}
                >
                  <Icon name={`${subItem.icon}`} />
                </View>
                <Text
                  className={`ml-4 text-base tracking-wide font-medium whitespace-nowrap xxl:hidden group-hover:block ${isSubActive ? "font-semibold" : ""}`}
                >
                  {t(subItem.label || "")}
                </Text>
              </Link>
            );
          })}
        </View>
      </View>
    </View>
  );
};

export default Collapsible;
